import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { fetchAssistantSettings } from "services/assistant-settings.service";
import { updateWorkSecuritySettings } from "services/work-security.service";
import { SettingRow, SettingsCard, Toggle, useSaveWithToast } from "../../ui";

interface AiQuestionsCardProps {
  companyId: string;
  enabled: boolean;
  /** Reloads the company so `enabled` shows what was just saved. */
  onSaved: () => Promise<unknown>;
}

/** Lets managers draft training test questions with the company's AI assistant. */
export default function AiQuestionsCard({
  companyId,
  enabled,
  onSaved,
}: AiQuestionsCardProps) {
  const { t } = useTranslation();
  const { save, saving } = useSaveWithToast();
  // null until the assistant's settings have loaded.
  const [assistantActive, setAssistantActive] = useState<boolean | null>(null);

  useEffect(() => {
    fetchAssistantSettings(companyId)
      .then((settings) =>
        setAssistantActive(Boolean(settings?.isActive && settings?.apiKey))
      )
      .catch(() => setAssistantActive(false));
  }, [companyId]);

  const change = (next: boolean) =>
    save(async () => {
      await updateWorkSecuritySettings(companyId, {
        workSecurityAiQuestionsEnabled: next,
      });
      await onSaved();
    });

  return (
    <SettingsCard>
      <SettingRow
        label={t("work_security_ai_questions_settings")}
        value={
          <>
            <p className="max-w-xl text-gray-600">
              <Trans
                i18nKey="work_security_ai_questions_settings_help"
                components={{
                  assistantLink: (
                    <Link
                      to={`/app/${companyId}/settings/assistant`}
                      className="font-medium text-indigo-600 hover:text-indigo-800"
                    />
                  ),
                }}
              />
            </p>
            {assistantActive === false && (
              <p className="mt-1 text-amber-800">
                {t("work_security_ai_questions_assistant_required")}
              </p>
            )}
          </>
        }
        action={
          <Toggle
            checked={assistantActive === true && enabled}
            onChange={change}
            label={t("work_security_ai_questions_settings")}
            disabled={assistantActive !== true || saving}
          />
        }
      />
    </SettingsCard>
  );
}
